/**
 * Convert a thrown error / Response into a user-friendly message.
 *
 * Two goals:
 *   1. Real auth failures (HTTP 4xx with a `detail`) → surface that to the user.
 *   2. Network / browser noise (extensions hooking fetch, "Failed to fetch",
 *      CORS, DNS, ad blockers, mixed content, offline) → surface an actionable
 *      "connection issue, please retry" so users don't think their password is
 *      wrong when it's really their wifi or an extension.
 *
 * Use the helpers below from any login / mutation handler.
 */

const NETWORK_PATTERNS = [
  "Failed to fetch",
  "NetworkError when attempting",
  "network request failed",
  "TypeError: Load failed",
  "ERR_NETWORK",
  "ERR_INTERNET_DISCONNECTED",
  "ERR_CONNECTION",
];

// Browser-extension / dev-tooling artefacts that are NOT user-visible failures.
const SILENT_NOISE = [
  "Request object could not be cloned",
  "PerformanceServerTiming",
  "could not be cloned",
  "DataCloneError",
  "body stream already read",
  "Failed to execute 'json' on 'Response'",
];

function _classify(err) {
  const msg = (err && (err.message || String(err))) || "";
  if (!msg) return { kind: "unknown", msg };
  for (const p of SILENT_NOISE) if (msg.indexOf(p) !== -1) return { kind: "silent", msg };
  for (const p of NETWORK_PATTERNS) if (msg.indexOf(p) !== -1) return { kind: "network", msg };
  if (!navigator.onLine) return { kind: "offline", msg };
  return { kind: "app", msg };
}

/** Auth-screen variant — preserves login UX. */
export function friendlyAuthError(err, fallback = "Invalid email or password") {
  const { kind, msg } = _classify(err);
  if (kind === "silent") return fallback;
  if (kind === "offline") return "You appear to be offline. Reconnect and try again.";
  if (kind === "network") return "Couldn't reach the server. Check your internet, disable ad blockers / VPNs, and try again.";
  return msg || fallback;
}

/** Generic variant for non-auth flows (forms, dashboards). */
export function friendlyError(err, fallback = "Something went wrong. Please try again.") {
  const { kind, msg } = _classify(err);
  if (kind === "silent") return fallback;
  if (kind === "offline") return "You appear to be offline. Reconnect and try again.";
  if (kind === "network") return "Couldn't reach the server. Please retry — if it keeps failing, try a different browser or disable ad blockers.";
  return msg || fallback;
}

/** Multi-file upload variant — adds large-file guidance. */
export function friendlyUploadError(err, fallback = "Upload failed. Please try again.") {
  const { kind, msg } = _classify(err);
  if (kind === "silent") return fallback;
  if (kind === "offline") return "You appear to be offline. Reconnect and try again.";
  if (kind === "network") {
    return (
      "Upload could not complete — your connection dropped before all files finished uploading. "
      + "Please check your internet, disable any VPN/ad-blocker, and retry. "
      + "If your files are large, compress images/scans to under 2 MB each."
    );
  }
  return msg || fallback;
}

/**
 * Wraps `fetch()` so callers get a clean rejection on HTTP errors AND a
 * normalised Error message for network failures.
 *
 *   const data = await jsonFetch('/api/foo', { method: 'POST', body: ... });
 *   // throws Error('Detail from server') on 4xx/5xx
 *   // throws Error('Couldn't reach the server…') on network failure
 */
export async function jsonFetch(url, options = {}) {
  let response;
  try {
    response = await fetch(url, { credentials: "include", ...options });
  } catch (err) {
    throw new Error(friendlyError(err));
  }
  let body = null;
  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : null;
  } catch (_) {
    body = null;
  }
  if (!response.ok) {
    const detail = (body && (body.detail || body.message)) || `Request failed (HTTP ${response.status})`;
    throw new Error(detail);
  }
  return body;
}
